import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreateManualUserParams {
  firstName: string;
  lastName?: string;
  dni: string;
  role: string;
  gymId: string;
  phone?: string;
  address?: string;
  emergencyPhone?: string;
  healthInsurance?: string;
}

export const useCreateManualUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ firstName, lastName, dni, role, gymId, phone, address, emergencyPhone, healthInsurance }: CreateManualUserParams) => {
      const { data: existingPending, error: pendingError } = await supabase
        .from("pending_members")
        .select("id")
        .eq("dni", dni)
        .eq("gym_id", gymId)
        .maybeSingle();

      if (pendingError) throw new Error("Error al verificar usuario pendiente");
      if (existingPending) throw new Error("Ya existe un usuario pendiente con este documento en el gimnasio");

      const { data: existingProfile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("dni", dni)
        .eq("gym_id", gymId)
        .maybeSingle();

      if (profileError) throw new Error("Error al verificar usuario existente");
      if (existingProfile) throw new Error("Ya existe un usuario registrado con este documento en el gimnasio");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No se pudo obtener el usuario actual");

      const { data, error } = await supabase
        .from("pending_members")
        .insert({
          first_name: firstName,
          last_name: lastName || null,
          dni,
          role,
          gym_id: gymId,
          created_by: user.id,
          phone: phone || null,
          address: address || null,
          emergency_phone: emergencyPhone || null,
          health_insurance: healthInsurance || null,
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating pending member:", error);
        throw new Error("Error al crear el usuario pendiente");
      }

      return data;
    },
    onSuccess: () => {
      toast.success("Usuario agregado correctamente. Pendiente de registro.");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["pending-members"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
